/**
 * The three PWA status banners, rendered above the app shell.
 *
 * - Offline — you are viewing the last synced version and editing is off.
 * - Update available — a new build is waiting; tapping Reload activates it.
 * - Ready offline — one-shot confirmation after the first precache.
 *
 * The update banner is the visible half of the prompt-mode service worker
 * contract in `src/pwa.ts`. Without it the new worker would sit waiting
 * indefinitely and deploys would never reach an installed app.
 */
import { useEffect, useState } from "react";
import { WifiOff, RefreshCw, CheckCircle2, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { useIsOffline } from "@/hooks/use-is-offline";
import { PWA_OFFLINE_READY, PWA_UPDATE_AVAILABLE, type PwaUpdateDetail } from "@/pwa";
import { cn } from "@/lib/utils";

export function StatusBanners() {
  const isOffline = useIsOffline();
  const [updateSW, setUpdateSW] = useState<PwaUpdateDetail["updateSW"] | null>(null);
  const [offlineReady, setOfflineReady] = useState(false);
  const [reloading, setReloading] = useState(false);

  useEffect(() => {
    const onUpdate = (event: Event) => {
      const detail = (event as CustomEvent<PwaUpdateDetail>).detail;
      // Store the callback in a closure — putting a function directly in
      // setState would have React call it as an updater.
      setUpdateSW(() => detail.updateSW);
    };
    const onReady = () => setOfflineReady(true);

    window.addEventListener(PWA_UPDATE_AVAILABLE, onUpdate);
    window.addEventListener(PWA_OFFLINE_READY, onReady);
    return () => {
      window.removeEventListener(PWA_UPDATE_AVAILABLE, onUpdate);
      window.removeEventListener(PWA_OFFLINE_READY, onReady);
    };
  }, []);

  if (!isOffline && !updateSW && !offlineReady) return null;

  return (
    <div
      className="sticky top-0 z-50"
      style={{ paddingTop: "env(safe-area-inset-top, 0px)" }}
    >
      {isOffline && (
        <Banner tone="warn" icon={WifiOff}>
          Offline — showing the last synced version. Editing is disabled.
        </Banner>
      )}

      {updateSW && (
        <Banner tone="info" icon={RefreshCw}>
          <span className="flex-1">A new version of Dashbase is available.</span>
          <Button
            size="sm"
            variant="secondary"
            disabled={reloading}
            onClick={() => {
              setReloading(true);
              // `true` activates the waiting worker and reloads the page.
              void updateSW(true);
            }}
          >
            {reloading ? "Reloading…" : "Reload"}
          </Button>
        </Banner>
      )}

      {offlineReady && !isOffline && (
        <Banner tone="good" icon={CheckCircle2}>
          <span className="flex-1">Ready to work offline.</span>
          <button
            type="button"
            aria-label="Dismiss"
            onClick={() => setOfflineReady(false)}
            className="p-1 rounded hover:bg-background/40"
          >
            <X className="w-3.5 h-3.5" />
          </button>
        </Banner>
      )}
    </div>
  );
}

function Banner({
  tone,
  icon: Icon,
  children,
}: {
  tone: "warn" | "info" | "good";
  icon: typeof WifiOff;
  children: React.ReactNode;
}) {
  return (
    <div
      role="status"
      className={cn(
        "flex items-center gap-2 px-4 py-2 text-xs border-b",
        tone === "warn" && "bg-amber-500/15 border-amber-500/30 text-amber-200",
        tone === "info" && "bg-sky-500/15 border-sky-500/30 text-sky-100",
        tone === "good" && "bg-primary/15 border-primary/30 text-primary",
      )}
      style={{
        paddingLeft: "calc(1rem + env(safe-area-inset-left, 0px))",
        paddingRight: "calc(1rem + env(safe-area-inset-right, 0px))",
      }}
    >
      <Icon className="w-3.5 h-3.5 shrink-0" />
      {children}
    </div>
  );
}
